import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Sync as SyncIcon,
  Science as TestIcon,
  School as SchoolIcon,
  TrendingUp as TrendsIcon,
  Assessment as AnalyticsIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUniversities,
  getUniversityDetails,
  createUniversity,
  updateUniversity,
  testUniversityConnection,
  syncUniversityData,
  getUniversityEnrollmentSummary,
  getUniversityTrends,
  getUniversityPrograms,
  getEnrollmentData,
  getSyncHistory,
  validateUniversityData,
  formatNumber,
  getLevelColor,
  type University,
  type CreateUniversityRequest,
  type UniversityEnrollment,
  type EnrollmentSummary,
} from '../../../services/universityApi';

const AUTH_TYPES = [
  { value: 'none', label: 'No Authentication' },
  { value: 'api_key', label: 'API Key' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'basic', label: 'Basic Auth' },
];

const EDUCATION_LEVELS = [
  { value: '', label: 'All Levels' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'bachelor', label: 'Bachelor' },
  { value: 'master', label: 'Master' },
  { value: 'phd', label: 'PhD' },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`university-tabpanel-${index}`}
      aria-labelledby={`university-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const UniversityEnrollmentForm: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [enrollmentFilters, setEnrollmentFilters] = useState({
    academic_year: '',
    level: '',
    program_code: '',
  });
  
  // Form state for creating new university
  const [newUniversity, setNewUniversity] = useState<CreateUniversityRequest>({
    name: '',
    code: '',
    api_base_url: '',
    enrollment_endpoint: '',
    auth_type: 'none',
    api_key: '',
    contact_email: '',
    description: '',
  });

  // Form state for editing university
  const [editUniversity, setEditUniversity] = useState<CreateUniversityRequest>({
    name: '',
    code: '',
    api_base_url: '',
    enrollment_endpoint: '',
    auth_type: 'none',
    api_key: '',
    contact_email: '',
    description: '',
  });

  const queryClient = useQueryClient();

  // Fetch universities
  const { data: universitiesData, isLoading: universitiesLoading, refetch: refetchUniversities } = useQuery({
    queryKey: ['universities'],
    queryFn: getUniversities,
  });

  // Fetch enrollment summary for selected university
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['university-summary', selectedUniversity?.id, enrollmentFilters.academic_year],
    queryFn: () => selectedUniversity ? 
      getUniversityEnrollmentSummary(selectedUniversity.id, enrollmentFilters.academic_year || undefined) : 
      Promise.resolve(null),
    enabled: !!selectedUniversity,
  });

  // Fetch enrollment data for selected university
  const { data: enrollmentData, isLoading: enrollmentLoading } = useQuery({
    queryKey: ['university-enrollments', selectedUniversity?.id, enrollmentFilters],
    queryFn: () => selectedUniversity ? 
      getEnrollmentData(selectedUniversity.id, enrollmentFilters) : 
      Promise.resolve(null),
    enabled: !!selectedUniversity,
  });

  // Fetch sync history for selected university
  const { data: syncHistoryData, isLoading: syncHistoryLoading } = useQuery({
    queryKey: ['sync-history', selectedUniversity?.id],
    queryFn: () => selectedUniversity ? getSyncHistory(selectedUniversity.id) : Promise.resolve(null),
    enabled: !!selectedUniversity,
  });

  // Create university mutation
  const createUniversityMutation = useMutation({
    mutationFn: createUniversity,
    onSuccess: () => {
      setCreateDialogOpen(false);
      setNewUniversity({
        name: '',
        code: '',
        api_base_url: '',
        enrollment_endpoint: '',
        auth_type: 'none',
        api_key: '',
        contact_email: '',
        description: '',
      });
      refetchUniversities();
    },
    onError: (error: any) => {
      console.error('Failed to create university:', error);
    },
  });

  // Update university mutation
  const updateUniversityMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateUniversityRequest> }) => 
      updateUniversity(id, data),
    onSuccess: () => {
      setEditDialogOpen(false);
      refetchUniversities();
    },
    onError: (error: any) => {
      console.error('Failed to update university:', error);
    },
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: testUniversityConnection,
    onSuccess: (data) => {
      console.log('Connection test result:', data);
    },
  });

  // Sync data mutation
  const syncDataMutation = useMutation({
    mutationFn: syncUniversityData,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['university-summary'] });
      queryClient.invalidateQueries({ queryKey: ['university-enrollments'] });
      queryClient.invalidateQueries({ queryKey: ['sync-history'] });
      refetchUniversities();
    },
  });

  const universities = universitiesData?.data || [];
  const summary = summaryData?.data;
  const enrollments = enrollmentData?.data || [];
  const syncHistory = syncHistoryData?.data || [];

  // Set first university as selected by default
  useEffect(() => {
    if (universities.length > 0 && !selectedUniversity) {
      setSelectedUniversity(universities[0]);
    }
  }, [universities, selectedUniversity]);

  const handleCreateUniversity = () => {
    const errors = validateUniversityData(newUniversity);
    if (errors.length > 0) {
      alert(`Validation errors:\n${errors.join('\n')}`);
      return;
    }
    createUniversityMutation.mutate(newUniversity);
  };

  const handleEditUniversity = async (university: University) => {
    try {
      const response = await getUniversityDetails(university.id);
      const details = response.data;
      setEditUniversity({
        name: details.name,
        code: details.code,
        api_base_url: details.api_base_url,
        enrollment_endpoint: details.enrollment_endpoint,
        auth_type: details.auth_type,
        api_key: details.api_key || '',
        contact_email: details.contact_email || '',
        description: details.description || '',
      });
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch university details:', error);
      alert('Failed to load university details for editing');
    }
  };

  const handleUpdateUniversity = () => {
    if (!selectedUniversity) return;
    
    const errors = validateUniversityData(editUniversity);
    if (errors.length > 0) {
      alert(`Validation errors:\n${errors.join('\n')}`);
      return;
    }
    
    updateUniversityMutation.mutate({
      id: selectedUniversity.id,
      data: editUniversity
    });
  };

  const handleTestConnection = (university: University) => {
    testConnectionMutation.mutate(university.id);
  };

  const handleSyncData = (university: University) => {
    syncDataMutation.mutate(university.id);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getLevelChipColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'phd':
        return 'error';
      case 'master':
        return 'secondary';
      case 'bachelor':
        return 'primary';
      case 'diploma':
        return 'info';
      case 'certificate':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            University Enrollment Integration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Connect and sync enrollment data from university APIs like SUZA
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Add University
        </Button>
      </Box>

      {/* University Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select University
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>University</InputLabel>
                <Select
                  value={selectedUniversity?.id || ''}
                  label="University"
                  onChange={(e) => {
                    const university = universities.find(u => u.id === Number(e.target.value));
                    setSelectedUniversity(university || null);
                  }}
                  disabled={universitiesLoading}
                >
                  {universities.map((university) => (
                    <MenuItem key={university.id} value={university.id}>
                      {university.name} ({university.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              {selectedUniversity && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip
                    icon={<SchoolIcon />}
                    label={`${selectedUniversity.total_programs} Programs`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    icon={<AnalyticsIcon />}
                    label={`${selectedUniversity.total_enrollments} Records`}
                    color="info"
                    variant="outlined"
                  />
                  <Chip
                    label={selectedUniversity.last_sync_status || 'Never synced'}
                    color={getStatusColor(selectedUniversity.last_sync_status || 'default')}
                    size="small"
                  />
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditUniversity(selectedUniversity)}
                    variant="outlined"
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<TestIcon />}
                    onClick={() => handleTestConnection(selectedUniversity)}
                    disabled={testConnectionMutation.isPending}
                  >
                    Test
                  </Button>
                  <Button
                    size="small"
                    startIcon={syncDataMutation.isPending ? <CircularProgress size={16} /> : <SyncIcon />}
                    onClick={() => handleSyncData(selectedUniversity)}
                    disabled={syncDataMutation.isPending}
                    variant="contained"
                  >
                    Sync Data
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedUniversity && (
        <>
          {/* Tabs */}
          <Paper elevation={1} sx={{ mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="university data tabs">
              <Tab label="Overview" icon={<AnalyticsIcon />} />
              <Tab label="Enrollment Data" icon={<SchoolIcon />} />
              <Tab label="Sync History" icon={<HistoryIcon />} />
            </Tabs>
          </Paper>

          <TabPanel value={tabValue} index={0}>
            {/* Overview Tab */}
            {summaryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : summary ? (
              <Grid container spacing={3}>
                {/* Summary Cards */}
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="primary">
                        Total Students
                      </Typography>
                      <Typography variant="h4">
                        {formatNumber(summary.total_students)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="info.main">
                        Total Programs
                      </Typography>
                      <Typography variant="h4">
                        {summary.total_programs}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="success.main">
                        Male Students
                      </Typography>
                      <Typography variant="h4">
                        {formatNumber(summary.total_male)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {summary.gender_distribution.male_pct}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" color="secondary.main">
                        Female Students
                      </Typography>
                      <Typography variant="h4">
                        {formatNumber(summary.total_female)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {summary.gender_distribution.female_pct}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Level Summary */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Enrollment by Education Level
                      </Typography>
                      <Grid container spacing={2}>
                        {Object.entries(summary.level_summary).map(([level, data]) => (
                          <Grid item xs={12} sm={6} md={4} key={level}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Chip
                                  label={level.toUpperCase()}
                                  color={getLevelChipColor(level)}
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                                <Typography variant="h6">
                                  {formatNumber(data.total)}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {data.programs} programs • {data.male} male • {data.female} female
                              </Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Alert severity="info">
                No enrollment data available. Try syncing data from the university API.
              </Alert>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Enrollment Data Tab */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Filter Enrollment Data
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Academic Year"
                      value={enrollmentFilters.academic_year}
                      onChange={(e) => setEnrollmentFilters(prev => ({ ...prev, academic_year: e.target.value }))}
                      placeholder="e.g., 2024/2025"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      select
                      label="Education Level"
                      value={enrollmentFilters.level}
                      onChange={(e) => setEnrollmentFilters(prev => ({ ...prev, level: e.target.value }))}
                    >
                      {EDUCATION_LEVELS.map((level) => (
                        <MenuItem key={level.value} value={level.value}>
                          {level.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Program Code"
                      value={enrollmentFilters.program_code}
                      onChange={(e) => setEnrollmentFilters(prev => ({ ...prev, program_code: e.target.value }))}
                      placeholder="e.g., BCS, MED, etc."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {enrollmentLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Academic Year</strong></TableCell>
                      <TableCell><strong>Level</strong></TableCell>
                      <TableCell><strong>Program</strong></TableCell>
                      <TableCell align="right"><strong>Total</strong></TableCell>
                      <TableCell align="right"><strong>Male</strong></TableCell>
                      <TableCell align="right"><strong>Female</strong></TableCell>
                      <TableCell><strong>Gender Ratio</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {enrollments.map((enrollment, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{enrollment.academic_year}</TableCell>
                        <TableCell>
                          <Chip
                            label={enrollment.level.toUpperCase()}
                            color={getLevelChipColor(enrollment.level)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {enrollment.program_code}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {enrollment.program_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight="bold">
                            {enrollment.total_students.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{enrollment.total_male.toLocaleString()}</TableCell>
                        <TableCell align="right">{enrollment.total_female.toLocaleString()}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                              label={`M: ${enrollment.gender_distribution.male_pct}%`}
                              color="info"
                              size="small"
                            />
                            <Chip
                              label={`F: ${enrollment.gender_distribution.female_pct}%`}
                              color="secondary"
                              size="small"
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {/* Sync History Tab */}
            {syncHistoryLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Duration</strong></TableCell>
                      <TableCell><strong>Records</strong></TableCell>
                      <TableCell><strong>Success Rate</strong></TableCell>
                      <TableCell><strong>Details</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncHistory.map((sync) => (
                      <TableRow key={sync.id} hover>
                        <TableCell>
                          {new Date(sync.started_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Chip label={sync.sync_type} variant="outlined" size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={sync.status}
                            color={getStatusColor(sync.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{sync.duration || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {sync.records_processed} processed
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {sync.records_created} created, {sync.records_updated} updated
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={sync.success_rate === 100 ? 'success.main' : 'warning.main'}>
                            {sync.success_rate}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {sync.error_message && (
                            <Tooltip title={sync.error_message}>
                              <ErrorIcon color="error" fontSize="small" />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </>
      )}

      {/* Edit University Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit University Configuration</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="University Name"
                value={editUniversity.name}
                onChange={(e) => setEditUniversity(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="University Code"
                value={editUniversity.code}
                onChange={(e) => setEditUniversity(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                required
                disabled
                helperText="Code cannot be changed"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Base URL"
                value={editUniversity.api_base_url}
                onChange={(e) => setEditUniversity(prev => ({ ...prev, api_base_url: e.target.value }))}
                required
                helperText="e.g., https://api.zu.ac.tz"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Enrollment Endpoint"
                value={editUniversity.enrollment_endpoint}
                onChange={(e) => setEditUniversity(prev => ({ ...prev, enrollment_endpoint: e.target.value }))}
                required
                helperText="e.g., /api/enrollment/data"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Authentication Type"
                value={editUniversity.auth_type}
                onChange={(e) => setEditUniversity(prev => ({ ...prev, auth_type: e.target.value as any }))}
              >
                {AUTH_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="API Key / Token"
                value={editUniversity.api_key}
                onChange={(e) => setEditUniversity(prev => ({ ...prev, api_key: e.target.value }))}
                disabled={editUniversity.auth_type === 'none'}
                type="password"
                helperText={editUniversity.auth_type === 'none' ? 'No authentication required' : 'Required for authenticated APIs'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                value={editUniversity.contact_email}
                onChange={(e) => setEditUniversity(prev => ({ ...prev, contact_email: e.target.value }))}
                type="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={editUniversity.description}
                onChange={(e) => setEditUniversity(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateUniversity}
            disabled={updateUniversityMutation.isPending}
            startIcon={updateUniversityMutation.isPending ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {updateUniversityMutation.isPending ? 'Updating...' : 'Update University'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create University Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New University</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="University Name"
                value={newUniversity.name}
                onChange={(e) => setNewUniversity(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="University Code"
                value={newUniversity.code}
                onChange={(e) => setNewUniversity(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                required
                helperText="e.g., SUZA, UDSM"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Base URL"
                value={newUniversity.api_base_url}
                onChange={(e) => setNewUniversity(prev => ({ ...prev, api_base_url: e.target.value }))}
                required
                helperText="e.g., https://api.suza.ac.tz"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Enrollment Endpoint"
                value={newUniversity.enrollment_endpoint}
                onChange={(e) => setNewUniversity(prev => ({ ...prev, enrollment_endpoint: e.target.value }))}
                required
                helperText="e.g., /api-gate-service/soas-admission/admission/data/enrolment-report"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Authentication Type"
                value={newUniversity.auth_type}
                onChange={(e) => setNewUniversity(prev => ({ ...prev, auth_type: e.target.value as any }))}
              >
                {AUTH_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="API Key / Token"
                value={newUniversity.api_key}
                onChange={(e) => setNewUniversity(prev => ({ ...prev, api_key: e.target.value }))}
                disabled={newUniversity.auth_type === 'none'}
                type="password"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                value={newUniversity.contact_email}
                onChange={(e) => setNewUniversity(prev => ({ ...prev, contact_email: e.target.value }))}
                type="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={newUniversity.description}
                onChange={(e) => setNewUniversity(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateUniversity}
            disabled={createUniversityMutation.isPending}
          >
            {createUniversityMutation.isPending ? <CircularProgress size={20} /> : 'Create University'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connection Test Result */}
      {testConnectionMutation.data && (
        <Alert 
          severity={testConnectionMutation.data.data.status === 'success' ? 'success' : 'error'}
          sx={{ mt: 2 }}
          onClose={() => testConnectionMutation.reset()}
        >
          {testConnectionMutation.data.data.message}
        </Alert>
      )}

      {/* Sync Result */}
      {syncDataMutation.data && (
        <Alert 
          severity="success"
          sx={{ mt: 2 }}
          onClose={() => syncDataMutation.reset()}
        >
          {syncDataMutation.data.message} - {syncDataMutation.data.data.records_created} created, {syncDataMutation.data.data.records_updated} updated
        </Alert>
      )}
    </Box>
  );
};

export default UniversityEnrollmentForm;